import { UserManager, OidcClient, User, WebStorageStateStore } from 'oidc-client-ts';
import { Capacitor } from '@capacitor/core';

// OIDC auth-code + PKCE client for the redwolffoundry IdP. Discovery is fetched from
// `${authority}/.well-known/openid-configuration`. The IdP is multi-tenant; the tenant is
// passed as an authorize-request query param. Mirrors admin-ui's services/oidc.js.
// Vue-CLI inlines process.env.VUE_APP_* at build time.
const issuer = process.env.VUE_APP_OIDC_ISSUER || 'https://auth-staging.redwolffoundry.com';
const clientId = process.env.VUE_APP_OIDC_CLIENT_ID || 'nomnomnomad';
const tenant = process.env.VUE_APP_OIDC_TENANT || 'nomnomnomad';

const isNative = Capacitor.isNativePlatform();

// In a Capacitor WebView the app is served from `capacitor://localhost`, which can't be a
// browser OAuth redirect target, and Apple requires the flow to run in an external browser.
// So native uses a *separate* public client whose redirect is a custom URL scheme deep link
// (registered in Info.plist + on the IdP), and drives the flow manually via @capacitor/browser
// + @capacitor/app instead of a same-window `signinRedirect`.
const nativeClientId = process.env.VUE_APP_OIDC_NATIVE_CLIENT_ID || 'nomnomnomad-native';
const nativeRedirectUri =
	process.env.VUE_APP_OIDC_NATIVE_REDIRECT_URI || 'com.redwolffoundry.nomnomnomad://callback';

// Shared settings for whichever platform we're on. The userStore is keyed by
// authority + client_id, so storeUser()/getUser() stay consistent as long as we build the
// request client (OidcClient) from these same settings.
const settings = {
	authority: issuer,
	client_id: isNative ? nativeClientId : clientId,
	redirect_uri: isNative ? nativeRedirectUri : `${window.location.origin}/callback`,
	post_logout_redirect_uri: isNative ? nativeRedirectUri : window.location.origin,
	response_type: 'code',
	scope: 'openid email profile offline_access',
	userStore: new WebStorageStateStore({ store: window.localStorage }),
	stateStore: new WebStorageStateStore({ store: window.localStorage }),
	// Silent renew uses a hidden iframe — unavailable in a native WebView.
	automaticSilentRenew: !isNative,
	...(tenant ? { extraQueryParams: { tenant } } : {})
};

export const userManager = new UserManager(settings);

// The Worker verifies the JWT against the IdP's JWKS. Use the id_token (signed JWT with
// aud=client_id) as the bearer — the IdP's access token may be opaque.
export async function getBearerToken() {
	let u = await userManager.getUser();
	// If the id_token has expired but we hold a refresh_token, refresh via the token
	// endpoint before giving up. This is the ONLY renewal path on native, where
	// automaticSilentRenew is off (no iframe in a WebView) — without it the session
	// would die at id_token expiry (~1h) and bounce the user to sign in every time.
	// The refresh_token grant needs no iframe, so it works on native and web alike.
	if (u && u.expired && u.refresh_token) {
		try {
			u = await userManager.signinSilent();
		} catch {
			u = null;
		}
	}
	return u && !u.expired ? u.id_token : null;
}

// --- Native (Capacitor) auth-code + PKCE via an external browser -------------------------

// Open the IdP authorize URL in the system browser, wait for the custom-scheme redirect to
// reopen the app, then exchange the code and persist the User into the shared userStore so the
// rest of the app (checkAuth/getBearerToken) works exactly as it does on web. Returns the User.
export async function nativeSignIn({ redirect = '/' } = {}) {
	const { Browser } = await import('@capacitor/browser');
	const { App: CapApp } = await import('@capacitor/app');

	const oidcClient = new OidcClient(settings);
	const request = await oidcClient.createSigninRequest({ state: { redirect } });

	// Register the deep-link listener *before* opening the browser so we can't miss the redirect.
	let handle;
	const callbackUrl = new Promise((resolve, reject) => {
		const timer = setTimeout(() => reject(new Error('Sign in timed out.')), 5 * 60 * 1000);
		CapApp.addListener('appUrlOpen', (event) => {
			if (event?.url && event.url.startsWith(nativeRedirectUri)) {
				clearTimeout(timer);
				resolve(event.url);
			}
		}).then((h) => (handle = h));
	});

	try {
		await Browser.open({ url: request.url, presentationStyle: 'popover' });
		const url = await callbackUrl;
		const response = await oidcClient.processSigninResponse(url);
		const user = new User({
			id_token: response.id_token,
			access_token: response.access_token,
			refresh_token: response.refresh_token,
			token_type: response.token_type,
			scope: response.scope,
			profile: response.profile,
			expires_at: response.expires_at,
			session_state: response.session_state
		});
		await userManager.storeUser(user);
		return user;
	} finally {
		if (handle) await handle.remove();
		try {
			await Browser.close();
		} catch {
			/* SFSafariViewController may already be dismissed by the redirect */
		}
	}
}

// Native sign-out. We deliberately DON'T do a browser RP-initiated end_session round trip here:
// the critical requirement is removing the cached User so a stale id_token can't silently
// re-authenticate us on the next checkAuth(). (End-session-in-browser can be added once a
// custom-scheme post_logout_redirect_uri is registered on the IdP.)
export async function nativeSignOut() {
	await userManager.removeUser();
}
